import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useMemo } from 'react';
import { schoolService } from '../../../services';
import {
  DEFAULT_ADMIN_LIST_SORT,
  normalizeAdminListSort,
} from '../../../lib/admin-list-sort';
import { SchoolStudentFilters, SchoolStudentCreateRequest, SchoolStudentUpdateRequest } from '../types';

// URL state parsers
const searchParser = parseAsString.withDefault('');
const schoolIdParser = parseAsString.withDefault('');
const sortParser = parseAsString.withDefault(DEFAULT_ADMIN_LIST_SORT);
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

export const useStudents = () => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [search, setSearch] = useQueryState('search', searchParser);
  const [schoolId, setSchoolId] = useQueryState('schoolId', schoolIdParser);
  const [sort, setSort] = useQueryState('sort', sortParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state
  const filters = useMemo(() => ({
    search: search || undefined,
    schoolId: schoolId || undefined,
    sort: normalizeAdminListSort(sort),
  }), [search, schoolId, sort]);

  // Query key cho caching
  const queryKey = ['school-students', filters, page, per_page];

  // Fetch students với react-query
  const {
    data: studentsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('🔍 Fetching students...', { filters, page, per_page });
      
      // Use single API endpoint with filters
      const response = await schoolService.getSchoolStudents({
        ...filters,
        page,
        per_page
      });
      
      // Use server pagination if available, otherwise create fallback
      let pagination;
      if (response.pagination) {
        pagination = {
          page: response.pagination.current_page || 1,
          per_page: response.pagination.per_page || 10,
          total: response.pagination.total || 0,
          totalPages: response.pagination.total_pages || 1
        };
      } else {
        pagination = {
          page: 1,
          per_page: response.data?.length || 0,
          total: response.data?.length || 0,
          totalPages: 1
        };
      }
      
      return {
        data: response.data || [],
        pagination
      };
    },
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Pagination info
  const pagination = useMemo(() => {
    if (!studentsData?.pagination) {
      return {
        page: 1,
        per_page: 10,
        total: 0,
        totalPages: 1
      };
    }
    
    return studentsData.pagination;
  }, [studentsData?.pagination]);

  // Create student mutation
  const createStudentMutation = useMutation({
    mutationFn: (data: SchoolStudentCreateRequest) => {
      return schoolService.createSchoolStudent(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Update student mutation
  const updateStudentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SchoolStudentUpdateRequest }) => {
      return schoolService.updateSchoolStudent(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Delete student mutation
  const deleteStudentMutation = useMutation({
    mutationFn: (id: string) => {
      return schoolService.deleteSchoolStudent(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Toggle student status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => {
      return schoolService.toggleSchoolStudentStatus(id, status);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
  });

  // Filter handlers
  const handleFilterChange = (newFilters: Partial<SchoolStudentFilters>) => {
    if (newFilters.search !== undefined) setSearch(newFilters.search || null);
    if (newFilters.schoolId !== undefined) setSchoolId(newFilters.schoolId || null);
    if (newFilters.sort !== undefined) setSort(normalizeAdminListSort(newFilters.sort));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch(null);
    setSchoolId(null);
    setSort(null);
    setPage(1);
  };

  return {
    // Data
    students: studentsData?.data || [],
    pagination,
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters: handleFilterChange,
    updatePagination: (page: number, perPage?: number) => {
      if (perPage !== undefined) {
        handlePerPageChange(perPage);
      } else {
        handlePageChange(page);
      }
    },
    resetFilters,

    // Mutations
    createStudent: createStudentMutation.mutateAsync,
    updateStudent: updateStudentMutation.mutateAsync,
    deleteStudent: deleteStudentMutation.mutateAsync,
    toggleStudentStatus: toggleStatusMutation.mutateAsync,

    // Mutation states
    isCreating: createStudentMutation.isPending,
    isUpdating: updateStudentMutation.isPending,
    isDeleting: deleteStudentMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,

    // Mutation objects for advanced handling
    createStudentMutation,
    updateStudentMutation,
    deleteStudentMutation,
    toggleStatusMutation,
  };
};
